import { AppCopy } from '@models/cms/app.model';
import { BagsCopy } from '@models/cms/bags.model';
import { ErrorsCopy } from '@models/cms/errors.model';
import { ExtrasCopy } from '@models/cms/extras.model';
import { FindTripsCopy } from '@models/cms/find-trips.model';
import { FlightsCopy } from '@models/cms/flights.model';
import { FooterCopy } from '@models/cms/footer.model';
import { HeaderCopy } from '@models/cms/header.model';
import { HomeCopy } from '@models/cms/home.model';
import { LoginCopy } from '@models/cms/login.model';
import { MealsCopy } from '@models/cms/meals.model';
import { PassengerTypesCopy } from '@models/cms/passenger-types.model';
import { PassengerCopy } from '@models/cms/passenger.model';
import { PaymentCopy } from '@models/cms/payment.model';
import { PurchaseConfirmationCopy } from '@models/cms/purchase-confirmation.model';
import { SeatsCopy } from '@models/cms/seats.model';
import { TripDetailsCopy } from '@models/cms/trip-details.model';
import { TripSummaryCopy } from '@models/cms/trip-summary.model';
import {
  CorporateResponsibilityCopy,
  ManagementTeamCopy,
  SustainabilityCopy,
} from '@models/cms/cms-dynamic/corporate/about/about.model';
import {
  AnalystsCoverageCopy,
  FinancialCalendarCopy,
  FinancialReportsCopy,
  PresentationsCopy,
  ShareHoldersCopy,
} from '@models/cms/cms-dynamic/corporate/investors/investors.model';
import { MediaLibraryCopy } from '@models/cms/cms-dynamic/corporate/media/media.model';
import {
  EconomyCopy,
  PremiumCopy,
} from '@models/cms/cms-dynamic/experience/cabins/cabins.model';
import { DreamLinersCopy } from '@models/cms/cms-dynamic/experience/experience.model';
import {
  HelpCenterCopy,
  LegalInformationCopy,
  OptionalServiceFeesCopy,
} from '@models/cms/cms-dynamic/support/support.model';
import { WhereWeFlyCopy } from '@models/cms/cms-dynamic/experience/where-we-fly.model';
import { NotFoundCopy } from '@models/cms/not-found.model';
import { DynamicPageCopy, DynamicPromoCodesPageCopy } from '@models/cms/cms-dynamic/dynamic-page.model';
import { BidForUpgradeCopy } from '@models/cms/cms-dynamic/experience/bid-for-upgrade.model';
import { TsaPrecheckCopy } from '@models/cms/cms-dynamic/support/tsa-precheck.model';
import {
  AwesomeEnumerationSection,
  ContentBlockSection,
  DestinationRoutesSection,
  DestinationRoutesSectionInteractiveMap,
  ExpandableParagraphSection,
  FaqSection,
  FareBrandCardsSection,
  HeroSection, PaddingSection, SearchWidgetSection,
  StickyNoteSection,
  TableSection,
  TableWithIconsSection
} from "@models/cms/dynamic-pages-umbraco/dynamic-pages-sections.model";

export type CmsFeatureCopy =
  | AppCopy
  | BagsCopy
  | HomeCopy
  | SeatsCopy
  | MealsCopy
  | ExtrasCopy
  | PassengerCopy
  | PaymentCopy
  | FlightsCopy
  | PurchaseConfirmationCopy
  | LoginCopy
  | ErrorsCopy
  | TripSummaryCopy
  | HeaderCopy
  | FooterCopy
  | TripDetailsCopy
  | PassengerTypesCopy
  | FindTripsCopy
  | NotFoundCopy;

export type CmsDynamicFeatureCopy =
  | CorporateResponsibilityCopy
  | SustainabilityCopy
  | ManagementTeamCopy
  | DynamicPageCopy
  | FinancialCalendarCopy
  | FinancialReportsCopy
  | PresentationsCopy
  | ShareHoldersCopy
  | AnalystsCoverageCopy
  | MediaLibraryCopy
  | EconomyCopy
  | PremiumCopy
  | DreamLinersCopy
  | WhereWeFlyCopy
  | HelpCenterCopy
  | OptionalServiceFeesCopy
  | LegalInformationCopy
  | BidForUpgradeCopy
  | DynamicPromoCodesPageCopy
  | TsaPrecheckCopy;

export type CmsDynamicPageSections =
  HeroSection
  | DestinationRoutesSection
  | DestinationRoutesSectionInteractiveMap
  | ContentBlockSection
  | StickyNoteSection
  | FaqSection
  | FareBrandCardsSection
  | AwesomeEnumerationSection
  | TableSection
  | TableWithIconsSection
  | ExpandableParagraphSection
  | SearchWidgetSection
  | PaddingSection;

export enum CmsDynamicPageSectionNames {
  HeroSection = 'HeroSection',
  DestinationRoutesSection = 'DestinationRoutesSection',
  DestinationRoutesSectionInteractiveMap = 'DestinationRoutesSectionInteractiveMap',
  ContentBlockSection = 'ContentBlockSection',
  StickyNoteSection = 'StickyNoteSection',
  FareBrandCardsSection = 'FareBrandCardsSection',
  FaqSection = 'FaqSection',
  AwesomeEnumerationSection = 'AwesomeEnumerationSection',
  TableSection = 'TableSection',
  TableWithIconsSection = 'TableWithIconsSection',
  ExpandableParagraphSection = 'ExpandableParagraphSection',
  SearchWidgetSection = 'SearchWidgetSection',
  PaddingSection = 'PaddingSection',
}

export type DynamicPageResponse = {
  cmsExternalId: string;
  cultureIsoCode: string;
  displayName: string;
  name: string;
  sections: CmsDynamicPageSections[];
}

export enum CmsKeys {
  // pages
  Bags = 'Bags',
  BoardingPass = 'BoardingPass',
  Confirmation = 'Confirmation',
  Documents = 'Documents',
  Experience = 'Experience',
  Extras = 'Extras',
  FlightStatus = 'FlightStatus',
  Flights = 'Flights',
  Meals = 'Meals',
  Payment = 'Payment',
  PurchaseCancellation = 'PurchaseCancellation',
  Review = 'Review',
  Seats = 'Seats',
  Travelers = 'Travelers',
  TripDetails = 'TripDetails',
  // flows / root pages
  App = 'App',
  Book = 'Book',
  Cancel = 'Cancel',
  CheckIn = 'CheckIn',
  Home = 'Home',
  Modify = 'Modify',
  Pay = 'Pay',
  Trips = 'Trips',
  Upgrade = 'Upgrade',
  NotFound = 'NotFound',
  Receipt = 'Receipt',
  Account = 'Account',
  PersonalInfo = 'PersonalInfo',
  TravelDocuments = 'TravelDocuments',
  TravelPreferences = 'TravelPreferences',
  ContactInfo = 'ContactInfo',
}

export enum CmsDynamicFlows {
  Corporate = 'Corporate',
  Experience = 'Experience',
  Support = 'Support',
  Menus = 'Menus',
  Promo = 'Promo',
  Destinations = 'Destinations',
}

export type CmsDynamicFlowsType = keyof typeof CmsDynamicFlows;

export enum CmsDynamicSubFlows {
  About = 'About',
  Media = 'Media',
  Investors = 'Investors',
  Cabins = 'Cabins',
  Dreamliners = 'Dreamliners',
  Sustainability = 'Sustainability',
  WhereWeFly = 'WhereWeFly',
  HelpCenter = 'HelpCenter',
  Baggage = 'Baggage',
  Infants = 'Infants',
  OptionalServiceFees = 'OptionalFees',
  PreFlightCheckList = 'PreFlightChecklist',
  LegalInformation = 'Legal',
  RestrictedItems = 'RestrictedItems',
  FareRules = 'FareRules',
  TermsAndConditions = 'TermsAndConditions',
  SocialMediaTermsAndConditions = 'SocialMediaTermsAndConditions',
  TsaPreCheck = 'TsaPreCheck',
  GlobalEntry = 'GlobalEntry',
  LinkedTravelAgreementCt = 'LinkedTravelAgreementCt',
}

export type CmsDynamicSubFlowsType = {
  [key in keyof typeof CmsDynamicSubFlows]?:
    | CmsDynamicKeysType
    | CmsDynamicFeatureCopy
    | null;
};

export enum CmsDynamicKeys {
  OurValues = 'OurValues',
  OurBrand = 'OurBrand',
  CompanyProfile = 'CompanyProfile',
  CorporateGovernance = 'CorporateGovernance',
  CorporateResponsibility = 'CorporateResponsibility',
  ManagementTeam = 'ManagementTeam',
  Sustainability = 'Sustainability',
  Imprint = 'Imprint',
  MediaLibrary = 'MediaLibrary',
  FinancialCalendar = 'FinancialCalendar',
  FinancialReports = 'FinancialReports',
  Presentations = 'Presentations',
  ShareHolders = 'Shareholders',
  AnalystCoverage = 'AnalystCoverage',
  Economy = 'Economy',
  Premium = 'Premium',
  Inflight = 'Inflight',
  BidForUpgrade = 'BidForUpgrade',
}

export type CmsDynamicKeysType = {
  [key in keyof typeof CmsDynamicKeys]?: CmsDynamicFeatureCopy | null;
};

export enum CmsSubKeys {
  appApiErrors = 'appApiErrors',
  availabilityFlightDetails = 'availabilityFlightDetail',
  baggage = 'baggage',
  bagsFareClassCard = 'fareClassCard',
  bagsSsrs = 'bagsSsrs',
  bidForUpgradeModals = 'bidForUpgradeModals',
  cancellationSummary = 'cancellationSummary',
  dangerousGoods = 'dangerousGoods',
  errors = 'errors',
  extrasSsrs = 'extrasSsrs',
  fareAvailabilityRibbon = 'fareAvailabilityRibbon',
  flightInformation = 'flightInformation',
  footer = 'footer',
  footerNavigation = 'footerNavigation',
  header = 'header',
  homeBanners = 'banners',
  homeExperience = 'experience',
  login = 'login',
  lowFareCalendar = 'lowFareCalendar',
  lowFareRibbon = 'lowFareRibbon',
  mealsSsrs = 'mealsSsrs',
  newFareBrandCards = 'newFareBrandCards',
  otherCodes = 'otherCodes',
  partnerTiles = 'partnerTiles',
  passenger = 'travelers',
  // hack to get passenger copy into trip details for the passenger form
  passengerTypes = 'passengerTypes',
  paymentMethods = 'paymentMethods',
  paidSummary = 'paidSummary',
  purchaseSummary = 'purchaseSummary',
  searchWidgetFull = 'searchWidgetFull',
  seatPosition = 'seatPosition',
  seatSelection = 'seatSelection',
  seatsMapIcon = 'seatsMapIcon',
  seatsSsrs = 'seatsSsrs',
  specialServicesGroups = 'specialServicesGroup',
  spoilageCodes = 'spoilageCodes',
  taxCodes = 'taxCodes',
  // moved to subkeys, will have to update CMS files
  tripSummary = 'tripSummary',
  cabinPromo = 'cabinPromo',
  cabinPromo2 = 'cabinPromos',
  homeUSPs = 'homeUSPs',
}

export const DYNAMIC = 'dynamic';

export enum ContentPageKeys {
  CheckInMessageBox = 'CheckInMessageBox',
  Skybid = 'Skybid',
  SkybidPayment = 'SkybidPayment',
  FlashSales = 'FlashSales',
  FareBrandCards = 'FareBrandCards',
  AnnounceBanners = 'AnnounceBanners',
  Deals = 'Deals',
}

export enum FeaturePageKeys {
  PriceDisplayPayCopy = '3241PriceDisplayOnPaymentPages',
  SkybidMMBHomePageV1 = '2801MMBHome',
  SkybidMMBHomePageV2 = '2818MMBHome',
  SkybidMMBHomePageV3 = '2986MMBHome',
  SkybidMMBHomePageV4 = '3126MMBHome',
  SkybidMMBHomePageV6 = '3128MMBHome',
  Deeplinks = '2474Deeplinks',
  CorporateCreditCards = '2430CorporateCreditCards',
  AppDownloadBanner = '3129AppDownloadBanner',
  YourTripsImprovement = '3240YourTripsImprovement',
  NavbarCopy = '3322TripNavbar',
  ClarifyingFinalStepOCI = '3399CheckinConfirmation',
  UpgradeNotAvailable = '3429UpgradeFlight'
}
